import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import { Button } from '../../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PhotoGalleryModal = ({ isOpen, onClose, files, token }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const blobRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !files?.length) return;
    setCurrentIndex(0);
  }, [isOpen, files]);

  useEffect(() => {
    if (!isOpen || !files?.length) return;
    const file = files[currentIndex];
    if (!file?.storage_path) return;
    setLoading(true);
    axios.get(`${API}/files/${file.storage_path}`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    }).then(res => {
      if (blobRef.current) URL.revokeObjectURL(blobRef.current);
      const url = URL.createObjectURL(res.data);
      blobRef.current = url;
      setBlobUrl(url);
    }).catch(() => setBlobUrl(null))
      .finally(() => setLoading(false));
    return () => {
      if (blobRef.current) { URL.revokeObjectURL(blobRef.current); blobRef.current = null; }
    };
  }, [currentIndex, isOpen, files, token]);

  if (!isOpen || !files?.length) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative max-w-3xl max-h-[85vh] w-full" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white hover:text-gray-300 z-10">
          <X size={28} />
        </button>
        <div className="bg-white rounded-lg overflow-hidden shadow-2xl">
          <div className="relative bg-gray-900 flex items-center justify-center min-h-[300px]">
            {loading ? (
              <div className="text-white text-sm animate-pulse">Cargando imagen...</div>
            ) : blobUrl ? (
              <img src={blobUrl} alt="Foto" className="max-h-[70vh] max-w-full object-contain" />
            ) : (
              <div className="text-gray-400 text-sm">No se pudo cargar la imagen</div>
            )}
            {files.length > 1 && (
              <>
                <button onClick={() => setCurrentIndex(i => (i - 1 + files.length) % files.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2">
                  <ChevronLeft size={24} />
                </button>
                <button onClick={() => setCurrentIndex(i => (i + 1) % files.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2">
                  <ChevronRight size={24} />
                </button>
              </>
            )}
          </div>
          <div className="p-3 bg-gray-50 flex items-center justify-between text-sm text-gray-600">
            <span>{files[currentIndex]?.original_filename || 'Imagen'}</span>
            <span>{currentIndex + 1} / {files.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CameraIcon = ({ trackingId, token, onClick }) => {
  const [hasPhotos, setHasPhotos] = useState(false);

  useEffect(() => {
    if (!trackingId || !token) return;
    axios.get(`${API}/packages/${trackingId}/images`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setHasPhotos(res.data?.length > 0))
      .catch(() => {});
  }, [trackingId, token]);

  if (!hasPhotos) return null;

  return (
    <button onClick={onClick} className="inline-flex items-center" title="Ver fotos del paquete">
      <Camera size={16} className="text-orange-500 hover:text-orange-600 cursor-pointer" />
    </button>
  );
};

export default PhotoGalleryModal;
